import { useState } from "react";
import { GuidanceMatrixDialog } from "@/components/dashboard/GuidanceMatrixDialog";
import { availabilityPercent, guidanceTier } from "@/components/dashboard/GuidanceStrip";
import { type MonthGuidance, useGuidance } from "@/hooks/use-guidance";

// Reuse the existing fit-tier palette — same scale as the picker strip (WI-002 AC).
const TIER_VARS: Record<ReturnType<typeof guidanceTier>, { bg: string; fg: string }> = {
  low: { bg: "var(--fit-strong-bg)", fg: "var(--fit-strong)" },
  moderate: { bg: "var(--fit-moderate-bg)", fg: "var(--fit-moderate)" },
  high: { bg: "var(--fit-at-risk-bg)", fg: "var(--fit-at-risk)" },
  none: { bg: "var(--muted)", fg: "var(--muted-foreground)" },
};

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function OutlookCell({ m, onClick }: { m: MonthGuidance; onClick?: () => void }) {
  const tier = guidanceTier(m.guidanceHours, m.capacityHours);
  const avail = availabilityPercent(m.guidanceHours, m.capacityHours);
  const { bg, fg } = TIER_VARS[tier];
  const label = MONTH_ABBR[m.month - 1] ?? "";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 cursor-pointer flex-col items-center gap-0.5 rounded-md px-2 py-2 text-center transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        m.isCurrent ? "ring-2 ring-brand-500" : ""
      }`}
      style={{ backgroundColor: bg, color: fg }}
      title={`${label} ${m.year}${m.isCurrent ? " (current month)" : ""} · guidance ${
        m.guidanceHours ?? "—"
      }h / capacity ${m.capacityHours ?? "—"}h — click for the Solution breakdown`}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{label}</span>
      <span className="font-display text-[18px] font-bold leading-none">
        {m.guidanceHours === null ? "–" : Math.round(m.guidanceHours)}
        <span className="text-[10px] font-medium opacity-70">h</span>
      </span>
      <span className="text-[11px] font-semibold leading-none">
        {avail === null ? "—" : `${avail}% free`}
      </span>
      <span className="text-[9px] leading-none opacity-60">
        {m.capacityHours === null ? "no cap" : `of ${Math.round(m.capacityHours)}h`}
      </span>
    </button>
  );
}

/**
 * Read-only monthly guidance + availability for a Resource (current + next 3),
 * sourced from NOS.
 *
 * Each tile is a button opening `GuidanceMatrixDialog` — the Solutions × months
 * breakdown that answers "which Solutions is this guidance for?", extended 3 months
 * back so past logged hours sit beside planned ones. The strip itself stays
 * person-level (guidance vs capacity); only the drill-down needs the per-Demand
 * endpoint, so the heavier read is deferred until a tile is clicked.
 */
export function GuidanceOutlook({
  resourceId,
  resourceName = "this Resource",
}: {
  resourceId: string;
  resourceName?: string;
}) {
  const { byResource, isLoading } = useGuidance();
  const months = byResource.get(resourceId);
  const [matrixOpen, setMatrixOpen] = useState(false);

  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Guidance Outlook
        </div>
        <span className="text-[10px] text-muted-foreground">from NOS · hrs / availability</span>
      </div>
      {isLoading ? (
        <div className="rounded-md border border-border bg-card p-3 text-[11px] text-muted-foreground">
          Loading guidance…
        </div>
      ) : !months || months.length === 0 ? (
        <div className="rounded-md border border-border bg-card p-3 text-[11px] italic text-muted-foreground">
          No NOS guidance for this Resource.
        </div>
      ) : (
        <>
          <div className="flex gap-1.5 rounded-md border border-border bg-card p-2">
            {months.slice(0, 4).map((m) => (
              <OutlookCell key={m.monthStart} m={m} onClick={() => setMatrixOpen(true)} />
            ))}
          </div>
          <div className="mt-1 text-center text-[10px] text-muted-foreground">
            Click a month for the Solution breakdown
          </div>
        </>
      )}
      <GuidanceMatrixDialog
        open={matrixOpen}
        onOpenChange={setMatrixOpen}
        resourceId={resourceId}
        resourceName={resourceName}
      />
    </section>
  );
}
